import { Component, OnInit, ViewChild, Renderer2, Inject, Input } from '@angular/core';
import { RolesService } from 'src/app/service/roles/roles.service';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { LocalstorageService } from 'src/app/service/shared/localstorage/localstorage.service';
import { ToolSettingsService } from 'src/app/service/tool-settings/tool-settings.service';
import { ToastService } from 'src/app/service/shared/toast/toast.service';
import { PermissionsService } from 'src/app/service/auth/permissions.service';
import { boolToInt } from 'src/app/utils/boolToInt';
import { setFormBuilderPermissionFields } from 'src/app/helpers/setFormBuilderPermissionFields';
import getAndParseUserPermissions from 'src/app/helpers/parsePermissions';

const viewAccess = { view: [1], edit: [0] }
const noPermissions = { view: [0], edit: [0] };

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss'],
})
export class RolesComponent implements OnInit {
  @ViewChild('closebutton') closebutton;
  @ViewChild('closebutton1') closebutton1;

  rolesData = [];
  roleNames = {};
  noDataMsg;
  permissionForm: FormGroup;
  deleteRoleId;
  searchInfo = {};
  userId;
  mode;
  userPermissions;
  @Input() roles_setting: any;
  assetClasses: any;

  constructor(
    private rolesService: RolesService,
    private formBuilder: FormBuilder,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private _document,
    private localStorageService: LocalstorageService,
    private toolSettingsService: ToolSettingsService,
    private toastService: ToastService,
    private permissionsService: PermissionsService,
  ) {
    this.onError = this.onError.bind(this);
    this.onGetPermissionError = this.onGetPermissionError.bind(this);
    this.onPermissionUpdateError = this.onPermissionUpdateError.bind(this);
    this.onPermissionUpdateSuccess = this.onPermissionUpdateSuccess.bind(this);
    this.onGetPermissionSuccess = this.onGetPermissionSuccess.bind(this);
    this.onSuccess = this.onSuccess.bind(this);
  }

  ngOnInit(): void {
    this.getRoles();
    this.createPermissionForm();
    this.userPermissions = getAndParseUserPermissions(this.localStorageService)
  }

  clearDatatable() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `clearDatatable('.role-data-table')`;
    this.renderer2.appendChild(this._document.body, s);
  }

  drawDatatable() {
    const s = this.renderer2.createElement('script');
    s.type = 'text/javascript';
    s.text = `reloadDataTable('.role-data-table')`;
    this.renderer2.appendChild(this._document.body, s);
  }

  createPermissionFormIfDataExists = data => {
    const permissions = JSON.parse(data.permissions);
    //Generate form group with permission data by id

    const excludedRoles = ['tools', 'tools_history', 'tool_models', 'asset_class'];
    const rolesArr = Object.keys(permissions).filter(key => !excludedRoles.includes(key.toLowerCase())) ?? [];

    const permissionsObj = {};

    rolesArr.map(key => {
      permissionsObj[key] = this.formBuilder.group(setFormBuilderPermissionFields(permissions, key));
    });

    this.permissionForm = this.formBuilder.group({
      permissions: this.formBuilder.group({
        ...permissionsObj,
        receiving: this.formBuilder.group({
          view: [boolToInt(permissions.receiving.view)],
          edit: [boolToInt(permissions.receiving.edit)]
        }),
        receiving_module_po_update_notify_owner: this.formBuilder.group({
          view: [boolToInt(permissions.receiving_module_po_update_notify_owner.view)],
          edit: [boolToInt(permissions.receiving_module_po_update_notify_owner.edit)],
        }),
        invoicing_reset: this.formBuilder.group({
          edit: [boolToInt(permissions.invoicing_reset.edit)],
        }),

        // Tools Permissions
        tools: this.formBuilder.group({
          view: [boolToInt(permissions.tools.view)],
          detailed_view: [boolToInt(permissions.tools.detailed_view)],
          edit: [boolToInt(permissions.tools.edit)],
          admin: [boolToInt(permissions.tools.admin)],
        }),
        tools_history: this.formBuilder.group({
          view: [boolToInt(permissions.tools_history.view)],
          detailed_view: [boolToInt(permissions.tools_history.detailed_view)],
        }),
        tool_models: this.formBuilder.group({
          view: [boolToInt(permissions.tool_models.view)],
          detailed_view: [boolToInt(permissions.tool_models.detailed_view)],
          edit: [boolToInt(permissions.tool_models.view)],
          admin: [boolToInt(permissions.tool_models.edit)],
        }),
        tool_inventory: this.formBuilder.group({
          view: [boolToInt(permissions.tool_inventory.view)],
          edit: [boolToInt(permissions.tool_inventory.edit)]
        }),
        asset_class: this.formBuilder.group({}),
      }),

      id: [data.id],
      role_name: [data.role_name, Validators.required],
      description: [data.description],
      updated_date: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, ''),
    });

    this.getActiveAssetClasses(permissions);
  };

  //To create permission form
  createPermissionForm(data = null) {
    if (!!data) {
      return this.createPermissionFormIfDataExists(data);
    }

    this.userId = this.localStorageService.getItem('userId');

    //Generate form group for add roles and permission
    this.permissionForm = this.formBuilder.group({
      permissions: this.formBuilder.group({
        main_job_list: this.formBuilder.group(viewAccess),
        general_info: this.formBuilder.group(viewAccess),
        billing_accounting: this.formBuilder.group(viewAccess),
        financials: this.formBuilder.group(noPermissions),
        documents: this.formBuilder.group(noPermissions),
        audit_trail: this.formBuilder.group(noPermissions),
        locked_fields: this.formBuilder.group(noPermissions),
        invoicing_list: this.formBuilder.group(viewAccess),
        invoicing_reset: this.formBuilder.group({ edit: [0] }),
        job_status: this.formBuilder.group(viewAccess),
        working_status: this.formBuilder.group(noPermissions),
        owner_info: this.formBuilder.group(noPermissions),
        customers: this.formBuilder.group(noPermissions),
        purchase_orders: this.formBuilder.group(noPermissions),
        po_amount: this.formBuilder.group(noPermissions),
        material_request: this.formBuilder.group(noPermissions),
        receiving: this.formBuilder.group(noPermissions),
        receiving_module_po_update_notify_owner: this.formBuilder.group(noPermissions),
        accounting: this.formBuilder.group(noPermissions),
        sage_export: this.formBuilder.group(noPermissions),
        vendors: this.formBuilder.group(noPermissions),
        roles_setting: this.formBuilder.group(noPermissions),
        system_users_setting: this.formBuilder.group(noPermissions),
        union_locals_setting: this.formBuilder.group(noPermissions),
        tools_setting: this.formBuilder.group(noPermissions),
        design_setting: this.formBuilder.group(noPermissions),
        smtp_setting: this.formBuilder.group(noPermissions),
        jobs: this.formBuilder.group(noPermissions),
        master_list: this.formBuilder.group(noPermissions),
        settings: this.formBuilder.group(noPermissions),

        // Tools Permissions
        tools: this.formBuilder.group({
          view: [0],
          detailed_view: [0],
          edit: [0],
          admin: [0],
        }),
        tools_history: this.formBuilder.group({
          view: [0],
          detailed_view: [0],
        }),
        tool_models: this.formBuilder.group({
          view: [0],
          detailed_view: [0],
          edit: [0],
          admin: [0],
        }),
        tool_inventory: this.formBuilder.group(noPermissions),
        asset_class: this.formBuilder.group({}),

        // JobPointe Permissions
        job_pointe: this.formBuilder.group(viewAccess),
        job_pointe_employees: this.formBuilder.group(viewAccess),
        job_pointe_configuration: this.formBuilder.group(viewAccess),
        job_board_attributes: this.formBuilder.group(noPermissions),
        job_board_schedules: this.formBuilder.group(noPermissions),
        job_board_tags: this.formBuilder.group(noPermissions),
        job_employee_job_board_tags: this.formBuilder.group(noPermissions),
        employee_job_board_attributes: this.formBuilder.group(noPermissions),
        manage_jobs: this.formBuilder.group(noPermissions),
      }),

      id: [''],
      role_name: ['', Validators.required],
      description: [''],
      created_date: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, ''),
      updated_date: new Date().toISOString().replace(/T/, ' ').replace(/\..+/, ''),
      created_by: this.userId,
    });

    this.getActiveAssetClasses(undefined);
  }

  //Set 0/1 on checkbox checked false/true
  setValue(event, formGroupName, key: string) {
    if (key === 'view') {
      if (!event.target.checked) {
        this.permissionForm.get(['permissions', formGroupName, 'edit'])?.setValue(0);
        this.permissionForm.get(['permissions', formGroupName, 'detailed_view'])?.setValue(0);
        this.permissionForm.get(['permissions', formGroupName, 'admin'])?.setValue(0);
      }
    } else if (key === 'detailed_view') {
      if (event.target.checked) {
        this.permissionForm.get(['permissions', formGroupName, 'view'])?.setValue(1);
      } else {
        this.permissionForm.get(['permissions', formGroupName, 'edit'])?.setValue(0);
        this.permissionForm.get(['permissions', formGroupName, 'admin'])?.setValue(0);
      }
    } else if (key === 'edit') {
      if (event.target.checked) {
        this.permissionForm.get(['permissions', formGroupName, 'view'])?.setValue(1);
        this.permissionForm.get(['permissions', formGroupName, 'detailed_view'])?.setValue(1);
      } else {
        if (formGroupName === 'receiving') {
          this.permissionForm.get(['permissions', formGroupName, 'view'])?.setValue(0);
        }
        this.permissionForm.get(['permissions', formGroupName, 'admin'])?.setValue(0);
      }
    } else {
      if (event.target.checked) {
        this.permissionForm.get(['permissions', formGroupName, 'view'])?.setValue(1);
        this.permissionForm.get(['permissions', formGroupName, 'edit'])?.setValue(1);
        this.permissionForm.get(['permissions', formGroupName, 'detailed_view'])?.setValue(1);
      }
    }
    this.permissionForm.get(['permissions', formGroupName, key]).setValue(boolToInt(event.target.checked));
  }

  //To get all roles data
  getRoles() {
    this.rolesService.getRoles(this.searchInfo, true, true).subscribe(this.onSuccess, this.onError);
  }

  //On success of api call
  onSuccess(result) {
    if (!!result?.data) {
      this.clearDatatable();
      this.rolesData = result.data;
      this.roleNames = this.rolesData.reduce((list, role) => {
        list[role.id] = role.role_name;
        return list;
      }, {});
      this.drawDatatable();
      return;
    }
    this.noDataMsg = result.errors;
  }

  //On error of api call
  onError(_: unknown) { }

  //To get edit permission data
  editPermission(item = null) {
    if (!!item) {
      this.mode = 'Edit';
      this.rolesService
        .getRoleById(item.id, true, true)
        .subscribe(this.onGetPermissionSuccess, this.onGetPermissionError);
      return;
    }

    this.mode = 'Add';
    this.createPermissionForm();
  }

  //On success of get role permission data by id
  onGetPermissionSuccess(result) {
    if (!!result?.data) this.createPermissionForm(result.data);
  }

  //On error of get role permission data by id
  onGetPermissionError(_: unknown) { }

  getActiveAssetClasses(permissions = null) {
    this.toolSettingsService.read['asset-classes']({
      bypassPermissions: 1,
      filter: ''
    }).subscribe(res => {
      this.assetClasses = res.data;
      this.createAssetClassForm(permissions);
    }, this.onError);
  }

  createAssetClassForm(permissions = null): void {
    this.assetClasses.forEach(assetClass => {
      const assetClassPermissions = permissions?.asset_class[assetClass.name]; //TODO: handle error incase assetclass permissions don't exist
      const assetClassForm = this.permissionForm.get('permissions').get('asset_class') as FormGroup;

      assetClassForm.addControl(
        assetClass.name,
        new FormGroup({
          view: new FormControl(assetClassPermissions ? assetClassPermissions['view'] : 0),
          detailed_view: new FormControl(assetClassPermissions ? assetClassPermissions['detailed_view'] : 0),
          edit: new FormControl(assetClassPermissions ? assetClassPermissions['edit'] : 0),
          admin: new FormControl(assetClassPermissions ? assetClassPermissions['admin'] : 0),
        }),
      );
    });
  }

  //Set 0/1 on checkbox checked false/true
  setAssetClassValue(event, formGroupName, key: 'view' | 'edit' | 'detailed_view' | 'admin') {
    const checked: boolean = event?.target?.checked || false;
    const assetClassPermissionsFormGroupSearch = ['permissions', 'asset_class', formGroupName];

    this.permissionForm.get([...assetClassPermissionsFormGroupSearch, key])?.setValue(boolToInt(checked));

    if (key === 'view') {
      if (!checked) {
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'detailed_view'])?.setValue(0);
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'edit'])?.setValue(0);
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'admin'])?.setValue(0);
      }
      return;
    } else if (key === 'detailed_view') {
      if (checked) {
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'view'])?.setValue(1);
      } else {
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'edit'])?.setValue(0);
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'admin'])?.setValue(0);
      }
      return;
    } else if (key === 'edit') {
      if (checked) {
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'view'])?.setValue(1);
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'detailed_view'])?.setValue(1);
      } else {
        this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'admin'])?.setValue(0);
      }
      return;
    }

    if (checked) {
      this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'view'])?.setValue(1);
      this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'detailed_view'])?.setValue(1);
      this.permissionForm.get([...assetClassPermissionsFormGroupSearch, 'edit'])?.setValue(1);
    }
  }

  getAssetClasses = () => this.permissionForm.get('permissions').get('asset_class')['controls'];

  //On submit role and permission form
  onSubmit(form) {
    if (form.invalid) {
      return;
    }

    //Check for if role has id or not
    if (!!form?.value?.id) {
      //Update role and permission if id exist
      this.rolesService
        .updateRole(form.value.id, form.value)
        .subscribe(this.onPermissionUpdateSuccess, this.onPermissionUpdateError);
      return;
    }

    //Add new role and permission if id does not exist
    delete form.value.id;
    this.rolesService.createRole(form.value).subscribe(this.onPermissionUpdateSuccess, this.onPermissionUpdateError);
  }

  //On success of add/update permission api
  onPermissionUpdateSuccess(result) {
    if (result) {
      if (result.data) {
        this.permissionsService.refresh();
        if (this.mode == 'Edit') {
          this.toastService.showToast('Role updated successfully', 'success');
        } else {
          this.toastService.showToast('Role added successfully', 'success');
        }
        this.closebutton.nativeElement.click();
        this.getRoles();
      }
    }
  }

  //On error of add/update permission api
  onPermissionUpdateError(_: unknown) { }

  //Delete permission based on id
  deletePermission(itemId: string) {
    if (!!itemId) {
      this.rolesService.deleteRole(itemId).subscribe(this.onPermissionDeleteSuccess, this.onPermissionDeleteError);
    }
  }

  //On success of delete permission api
  onPermissionDeleteSuccess(result) {
    if (!!result?.data) {
      this.closebutton1.nativeElement.click();
      this.toastService.showToast(result.data.message, 'success');
      this.getRoles();
    }
  }

  //On error of delete permission api
  onPermissionDeleteError(_: unknown) { }

  //Set role id to delete on confirm delete modal
  setDeleteRoleId = (id: string) => (this.deleteRoleId = id);

  //Set search parameter and get filter data
  searchInTable(value: string, key: string) {
    if (!!value) delete this.searchInfo[key];

    this.searchInfo[key] = value;
    this.getRoles();
  }
}
